import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import {
  useRetmBranchMappings,
  useImportRetmOrders,
} from "src/api/integrations/retm";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const RetmJourneyImport = () => {
  const { t } = useTranslation("integrations");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const setNotifications = useSetRecoilState(notificationsState);

  const [selectedBranchId, setSelectedBranchId] = useState<number | "">("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [importResult, setImportResult] = useState<{
    journey_id: number;
    start_date: string;
    end_date: string;
  } | null>(null);

  const { data: mappings, isLoading: mappingsLoading } =
    useRetmBranchMappings();
  const importOrdersMutation = useImportRetmOrders();

  const mappedBranches = mappings?.data || [];
  const isFormValid =
    selectedBranchId !== "" &&
    startDate !== "" &&
    endDate !== "" &&
    endDate >= startDate;
  const isImporting = importOrdersMutation.status === "pending";

  const selectedMapping = mappedBranches.find(
    (m) => m.internal_id === selectedBranchId
  );

  const handleImport = () => {
    if (!selectedBranchId || !startDate || !endDate) return;

    setImportResult(null);
    importOrdersMutation.mutate(
      {
        branch_id: selectedBranchId as number,
        start_date: startDate,
        end_date: endDate,
      },
      {
        onSuccess: (response) => {
          setImportResult(response.data);
          setNotifications((prev) => [
            ...prev,
            {
              message: t("orders.importSuccess", {
                journeyId: response.data.journey_id,
                startDate: response.data.start_date,
                endDate: response.data.end_date,
              }),
              type: "success",
            },
          ]);
        },
        onError: (error) => {
          const errorMsg =
            (error as any)?.data?.error ||
            (error as any)?.data?.message ||
            t("orders.importFailedRetm");
          setNotifications((prev) => [
            ...prev,
            {
              message: errorMsg,
              type: "error",
            },
          ]);
        },
      }
    );
  };

  return (
    <Stack gap={3} p={{ xs: 2, sm: "8px 32px 32px 32px" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/integrations/retm")}
            sx={{ color: "#667085" }}
          >
            {generalT("back")}
          </Button>
          <Box>
            <Typography fontSize={24} fontWeight={600} color="#101828">
              {t("orders.retmImportTitle")}
            </Typography>
            <Typography fontSize={14} color="#667085" mt={0.5}>
              {t("orders.retmImportSubtitle")}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box bgcolor="#F2F4F7" width="100%" height="1px" />

      {!mappingsLoading && mappedBranches.length === 0 && (
        <Alert severity="warning" sx={{ borderRadius: "8px" }}>
          {t("orders.noMappedBranchesRetm")}
        </Alert>
      )}

      <Card variant="outlined" sx={{ borderRadius: "12px" }}>
        <CardContent>
          <Stack spacing={3}>
            <Typography fontSize={16} fontWeight={600} color="#101828">
              {t("orders.importOrders")}
            </Typography>

            <FormControl fullWidth>
              <InputLabel>{t("orders.selectBranch")}</InputLabel>
              <Select
                value={selectedBranchId}
                label={t("orders.selectBranch")}
                onChange={(e) =>
                  setSelectedBranchId(e.target.value as number)
                }
                disabled={mappingsLoading || mappedBranches.length === 0}
              >
                <MenuItem value="" disabled>
                  {t("orders.selectBranch")}
                </MenuItem>
                {mappedBranches.map((mapping) => (
                  <MenuItem key={mapping.id} value={mapping.internal_id}>
                    <Stack>
                      <Typography fontSize={14}>
                        {mapping.internal_name}
                      </Typography>
                      <Typography fontSize={12} color="#667085">
                        RETM: {mapping.external_name || mapping.external_id}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                type="date"
                label={t("orders.startDate")}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                type="date"
                label={t("orders.endDate")}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: startDate || undefined }}
                fullWidth
              />
            </Stack>

            {startDate && endDate && endDate < startDate && (
              <Alert severity="error" sx={{ borderRadius: "8px" }}>
                {t("orders.dateRangeRequired")}
              </Alert>
            )}

            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={
                  isImporting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <FileUploadIcon />
                  )
                }
                onClick={handleImport}
                disabled={!isFormValid || isImporting}
                sx={{ minWidth: 180 }}
              >
                {isImporting
                  ? t("orders.importingOrders")
                  : t("orders.importOrders")}
              </Button>
            </Stack>

            {isImporting && (
              <Alert severity="info" sx={{ borderRadius: "8px" }}>
                {t("orders.importingOrders")}
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>

      {importResult && (
        <Card
          variant="outlined"
          sx={{ borderRadius: "12px", borderColor: "#12B76A" }}
        >
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              <CheckCircleIcon sx={{ color: "#12B76A", fontSize: 32 }} />
              <Box>
                <Typography fontSize={16} fontWeight={600} color="#101828">
                  {t("orders.importSuccess", {
                    journeyId: importResult.journey_id,
                    startDate: importResult.start_date,
                    endDate: importResult.end_date,
                  })}
                </Typography>
                {selectedMapping && (
                  <Typography fontSize={13} color="#667085" mt={0.5}>
                    {selectedMapping.internal_name} →{" "}
                    {selectedMapping.external_name}
                  </Typography>
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {importOrdersMutation.status === "error" && (
        <Alert severity="error" sx={{ borderRadius: "8px" }}>
          {(importOrdersMutation.error as any)?.data?.error ||
            t("orders.importFailedRetm")}
        </Alert>
      )}
    </Stack>
  );
};

export default RetmJourneyImport;
